import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { AddressService, StateOption } from '../../../../shared/services/address.service';
import { ContractTemplate } from '../../../models/contract-template.model';
import { ContractTemplateService } from '../../../services/contract-template.service';

@Component({
  selector: 'app-contract-template-create-or-update',
  standalone: true,
  templateUrl: './contract-template-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ProgressSpinnerModule,
    ToastModule,
    ToolbarModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
  ],
})
export class ContractTemplateCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private formGroupService = inject(FormGroupService);
  private addressService = inject(AddressService);
  private contractTemplateService = inject(ContractTemplateService);

  readonly countries = [{ value: 'US', label: 'Estados Unidos' }];
  readonly states = this.addressService.getStates();
  readonly acceptedFileExtensions =
    '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  contractTemplateId = signal<string | null>(null);
  loading = signal(false);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    country: [{ value: 'US', disabled: true }, [Validators.required]],
    state: [null, [Validators.required]],
    file: [null, [Validators.required]],
    fileId: [null],
    fileName: [null],
    fileUrl: [null],
  });

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');

    if (id) {
      this.contractTemplateId.set(id);
      this.loadData(id);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!this.isWordFile(file)) {
      input.value = '';
      this.formGroup.get('file')?.setValue(
        this.formGroup.get('fileName')?.value ?? null,
      );
      this.formGroup.get('file')?.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione apenas um arquivo do Word (.doc ou .docx).',
        life: 5000,
      });
      return;
    }

    this.formGroup.patchValue({ file });
    this.formGroup.get('file')?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const id = this.contractTemplateId();
    const formData = this.buildFormData();
    this.loading.set(true);

    const request$ = id
      ? this.contractTemplateService.update(id, formData)
      : this.contractTemplateService.create(formData);

    request$.subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Contrato salvo com sucesso',
          life: 2000,
        });
        this.router.navigate(['/contract-templates']);
      },
      error: (e) => {
        this.loading.set(false);
        this.handleSaveError(e);
      },
    });
  }

  get selectedFileName(): string | null {
    const value = this.formGroup.get('file')?.value;
    return value instanceof File ? value.name : value;
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.contractTemplateService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as ContractTemplate;
        this.formGroup.patchValue({
          id: model.id,
          country: 'US',
          state: model.state,
          file: model.fileName ?? model.fileId ?? null,
          fileId: model.fileId ?? null,
          fileName: model.fileName ?? null,
          fileUrl: model.fileUrl ?? null,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o contrato',
            life: 3000,
          });
        }
      },
    });
  }

  private buildFormData(): FormData {
    const value = this.formGroup.getRawValue();
    const formData = new FormData();

    formData.append('country', value.country);
    formData.append('state', this.getStateValue(value.state));

    if (value.fileId) formData.append('fileId', value.fileId);

    if (value.file instanceof File) {
      formData.append('file', value.file, value.file.name);
    }

    return formData;
  }

  private getStateValue(state: string | StateOption): string {
    return typeof state === 'string' ? state : state?.value ?? '';
  }

  private isWordFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.doc') || name.endsWith('.docx');
  }

  private handleSaveError(error: unknown): void {
    if (!(error instanceof HttpErrorResponse)) {
      this.showGenericSaveError();
      return;
    }

    const apiError = error.error ?? {};
    const serverErrors = apiError.errors;
    const hasServerErrors =
      serverErrors && Object.keys(serverErrors).length > 0;

    if (hasServerErrors) {
      this.formGroupService.addServerErrors(serverErrors, this.formGroup);
      return;
    }

    if (apiError.message) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: apiError.message,
        life: 4000,
      });
      return;
    }

    this.showGenericSaveError();
  }

  private showGenericSaveError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Falha',
      detail: 'Ocorreu um erro ao salvar o contrato',
      life: 3000,
    });
  }
}
